import { useCallback, useEffect, useMemo, useState } from "react";
import { bool, func, number } from "prop-types";
import * as yup from "yup";
import { observer } from "mobx-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getName } from "@cospired/i18n-iso-languages";
import { pick } from "lodash";
import { useNavigate } from "react-router-dom";
import { generateWorkTypeId } from "@repo/structure";
import { yupResolver } from "@hookform/resolvers/yup";
import { editor } from "@paths";
import { COMPANY_SETTINGS } from "@client";
import {
  addProjectAttachmentQuery,
  checkTemplateCommentsQuery,
  createNewAiProjectQuery,
  createNewProjectQuery,
  getCompanyRateCardQuery,
  getCompanySettingsQuery,
  getTemplateQuery,
} from "@query";
import { useActiveProjectStore, useS3Uploader, useStores } from "@hooks";
import { DEFAULT_WORK_TYPES, getLanguageList, MAX_ESTIMATE_TITLE_LENGTH } from "@utils";
import { Alert, Button, ButtonSwitch, Checkbox, Dialog, TooltipIcon } from "@components";
import { CircularProgress, useTheme } from "@material-ui/core";
import { ManualCreator } from "./ManualCreator/ManualCreator";
import { AICreator } from "./AICreator/AICreator";
import Grid from "@material-ui/core/Grid";

const CREATOR_VARIANTS = {
  MANUAL: "manual",
  AI: "ai"
}

export const NewProjectDialog = observer(({
  open,
  onClose,
  useAICreator,
  defaultTemplateId: remoteDefaultTemplateId,
  useTemplatesDialog,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userStore, stateStore } = useStores();
  const store = useActiveProjectStore(true);
  const theme = useTheme();
  const { uploadAiAttachment } = useS3Uploader();
  
  const [ variant, setVariant ] = useState(CREATOR_VARIANTS.MANUAL);
  const [ importTemplate, setImportTemplate ] = useState(false);
  const [ errorMessage, setErrorMessage ] = useState();
  const [ workTypesLoading, setWorkTypesLoading ] = useState(false);
  const [ defaultTemplateId, setDefaultTemplateId ] = useState(remoteDefaultTemplateId);
  // const [hasAccess, setHasAccess] = useState(remoteDefaultTemplateId);
  
  // const { checkAccess, FeatureAlert } = useFeatureGuardian(
  //   LOCKED_FEATURES.AI_PROJECT,
  // );
  
  const { workspaceUuid, userUuid, companyId } = userStore;
  const {
    hasXlsFile,
    status,
    availableWorkTypes,
    xlsProjectStructure,
    template,
    projectWorkTypes,
  } = store;
  
  const languageOptions = useMemo(() => getLanguageList(t, stateStore.lang), [stateStore.lang])
  
  const newProjectFormSchema = useCallback(() => (
    yup.object().shape({
      name: yup
        .string()
        .required(t("errors.project.name.required"))
        .min(4, t("errors.project.name.min"))
        .max(MAX_ESTIMATE_TITLE_LENGTH, t("errors.project.name.max")),
      description: yup.string(),
      workTypes: yup
        .array()
        .of(yup.mixed())
        // .min(1, t("errors.project.workType.required"))
        // .required(t("errors.project.workType.required"))
        .default([]),
      copyComments: yup.boolean(),
      generateScope: yup.boolean(),
      generateDescriptions: yup.boolean(),
      aiPrompt: variant === CREATOR_VARIANTS.AI
        ? yup
          .string()
          .when("promptFiles", {
            is: (v) => v.length === 0,
            then: () => yup
              .string()
              .min(1, t("errors.project.ai_prompt.required"))
              .required(t("errors.project.ai_prompt.required"))
          })
        : yup.string(),
      generateEstimates: yup.boolean(),
      generatorLevel: yup.number().required(),
      generatorLanguage: yup.string().required(),
      promptFiles: yup.array().of(yup.mixed()),
    })
  ), [variant]);
  
  const {
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(newProjectFormSchema()),
    defaultValues: {
      name: "",
      description: "",
      aiPrompt: "",
      workTypes: DEFAULT_WORK_TYPES,
      copyComments: true,
      generateScope: true,
      generateDescriptions: true,
      generateEstimates: true,
      generatorLevel: 3,
      generatorLanguage: languageOptions[0].id,
      promptFiles: [],
    },
  });
  
  useEffect(() => {
    setValue("workTypes", projectWorkTypes);
  }, [projectWorkTypes]);
  
  useEffect(() => {
    setWorkTypesLoading(true);
    (async () => {
      const defaultTemplateId = useTemplatesDialog
        ? await getCompanySettingsQuery(COMPANY_SETTINGS.TEMPLATE)
        : remoteDefaultTemplateId;
      
      let workTypes = store.workTypes;
      if (!workTypes.length) {
        workTypes = store?.workTypes || await getCompanyRateCardQuery();
        store.setWorkTypes(workTypes);
      }
      
      let wTSet = Boolean(projectWorkTypes?.length);
      if(defaultTemplateId && !store.template) {
        const hasComments = checkTemplateCommentsQuery(defaultTemplateId);
        
        setDefaultTemplateId(defaultTemplateId);
        const template = await getTemplateQuery(defaultTemplateId);
        if(template)
          wTSet = store.setTemplate({
            id: defaultTemplateId,
            content: template.description || template.content,
            hasComments,
            ...template
          }, template.company);
      }
      
      if(!wTSet)
        store.setDefaultProjectWorkTypes()
      
      setWorkTypesLoading(false);
    })();
    
    return () => {
      store.clearProjectData();
    };
  }, []);
  
  const handleSelectVariant = async (variant) => {
    // if(variant === CREATOR_VARIANTS.AI && !hasAccess) {
    //   const allowed = await checkAccess();
    //   setHasAccess(allowed)
    //   if (!allowed) return;
    // }
    
    setVariant(variant);
  }

  const addCustomWorkType = (name) => {
    const newWT = {
      id: generateWorkTypeId(),
      name,
      custom: true,
      backgroundColor: theme.palette.primary.main,
    }
    return store.addWorkType(newWT);
  };
  
  const saveProject = async (data) => {
    if(variant === CREATOR_VARIANTS.MANUAL)
      await saveManualProject(data);
    else if(variant === CREATOR_VARIANTS.AI)
      await saveAIProject(data);
  }
  
  const getWorkTypes = (workTypes) => (
    availableWorkTypes
      .filter(wT => workTypes.includes(wT.id))
      .map(wT => pick(wT, ["id", "name", "backgroundColor", "salesRate", "costRate"]))
  )
  
  const saveManualProject = async ({ name, description, workTypes, copyComments }) => {
    try {
      const {
        uuid,
      } = await createNewProjectQuery(
        userUuid,
        workspaceUuid,
        companyId,
        getWorkTypes(workTypes),
        name,
        description,
        status,
        template?.template?.MLP?.id
          || template?.template?.MVP?.id
          || template?.template?.MMP?.id
          || template?.id
          || undefined,
        xlsProjectStructure,
        importTemplate,
        copyComments,
      );
      
      navigate(editor(uuid))
      store.clearProjectData();
    } catch (err) {
      if (err.response && err.response.data && err.response.data.error)
        setErrorMessage(err.response.data.error);
      else if (err.message) setErrorMessage(err.message);
    }
  };
  
  const saveAIProject = async ({
    name,
    workTypes,
    aiPrompt,
    generateDescriptions,
    generateEstimates,
    generatorLevel,
    generatorLanguage,
    promptFiles
  }) => {
    
    const files = [];
    for(let file of promptFiles) {
      const res = await uploadAiAttachment(new Date().getTime(), file, file.name);
      files.push({ ...res, name: file.name });
    }
    
    const {
      uuid,
    } = await createNewAiProjectQuery(
      userUuid,
      workspaceUuid,
      companyId,
      getWorkTypes(workTypes),
      name,
      status,
      aiPrompt,
      generatorLevel,
      generatorLanguage === "detect"
        ? generatorLanguage
        : getName(generatorLanguage, "en"),
      generateDescriptions,
      generateEstimates,
      files.map(f => ({ name: f.name, url: f.Location })),
    );

    for(let file of files) {

       await addProjectAttachmentQuery(
        uuid,
        file.ETag.replace(/"/g, ""),
        file.name,
        file.Location,
        file.Key
      );
    }

    navigate(editor(uuid))
    store.clearProjectData();
  }
  
  return (
    <Dialog
      id="newProject"
      open={open}
      onClose={onClose}
      actionsClass="newProjectForm"
      containerId="newProjectForm"
      containerClass="newProjectForm py-3"
      actionsAlignment={hasXlsFile ? "spaceBetween" : "end"}
      width={600}
      title={t("views.new_project.title")}
      actions={
        <>
          {hasXlsFile && (
            <Checkbox
              name="importTemplate"
              checked={importTemplate}
              onChange={() => setImportTemplate(!importTemplate)}
              label={t("views.new_project.import_template")}
            />
          )}
          <Button
            variant="contained"
            onClick={handleSubmit(saveProject)}
            endIcon={
              isSubmitting ? (
                <CircularProgress
                  aria-label="progress indicator"
                  size={24}
                  color="inherit"
                />
              ) : undefined
            }
          >
            {t("views.new_project.title_short")}
          </Button>
        </>
      }
      titleActions={useAICreator && <Grid item container alignItems="center" className="w-max">
        <ButtonSwitch
          size="small"
          value={variant}
          setValue={handleSelectVariant}
          values={[
            { value: CREATOR_VARIANTS.MANUAL, label: t("views.new_project.creator_title.manual") },
            { value: CREATOR_VARIANTS.AI, label: t("views.new_project.creator_title.ai") },
          ]}
        />
        <TooltipIcon
          size="small"
          color="primary"
          className="ml-1"
        >
          <p className="text-sm">{t("views.new_project.ai_info.start")}</p>
          <p className="text-sm">{t("views.new_project.ai_info.f1")}</p>
          <p className="text-sm">{t("views.new_project.ai_info.f2")}</p>
          <p className="text-sm">{t("views.new_project.ai_info.f3")}</p>
        </TooltipIcon>
      </Grid>}
    >
      {
        variant === CREATOR_VARIANTS.MANUAL &&
        <ManualCreator
          onClose={onClose}
          control={control}
          setValue={setValue}
          workTypesLoading={workTypesLoading}
          defaultTemplateId={defaultTemplateId}
          useTemplatesDialog={useTemplatesDialog}
          addCustomWorkType={addCustomWorkType}
        />
      }
      {
        variant === CREATOR_VARIANTS.AI &&
        <AICreator
          onClose={onClose}
          control={control}
          setValue={setValue}
          watch={watch}
          workTypesLoading={workTypesLoading}
          addCustomWorkType={addCustomWorkType}
          languageOptions={languageOptions}
        />
      }
      
      <Alert
        isOpen={Boolean(errorMessage)}
        title={errorMessage}
        acceptText={t("common.accept")}
        onAccept={() => setErrorMessage()}
      />
      {/*<FeatureAlert />*/}
    </Dialog>
  );
});

NewProjectDialog.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  useAICreator: bool,
  
  useTemplatesDialog: bool,
  defaultTemplateId: number,
};
